import { useEffect, useState } from "react";
import { Pencil, Trash2, Eye, EyeOff, Star, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useDragReorder } from "@/lib/useDragReorder";
import Modal from "@/components/admin/Modal";
import { PageHeader, Field } from "./AdminFaq";

const EMPTY = { name: "", role: "", quote: "", photo_url: "", featured: false, order: 0, published: true, niche: "" };

const NICHE_OPTIONS = [
    { value: "", label: "— sem nicho específico —" },
    { value: "doces", label: "Doces" },
    { value: "gelados", label: "Gelados" },
    { value: "churros", label: "Churros" },
    { value: "cafes", label: "Cafés" },
    { value: "paes", label: "Pães" },
    { value: "drinks", label: "Drinks" },
    { value: "jornais", label: "Jornais" },
    { value: "sanduiches", label: "Sanduíches" },
    { value: "sushis", label: "Sushis" },
    { value: "pipocas", label: "Pipocas" },
    { value: "crepes", label: "Crepes" },
];

export default function AdminTestimonials() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try { const { data } = await api.get("/admin/testimonials"); setItems(data || []); }
        catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    function openCreate() { setEditing(null); setForm({ ...EMPTY, order: items.length + 1 }); setModalOpen(true); }
    function openEdit(it) { setEditing(it); setForm({ ...it }); setModalOpen(true); }

    async function onSave(e) {
        e.preventDefault(); setSaving(true);
        try {
            const payload = { ...form, photo_url: form.photo_url?.trim() || null };
            if (editing) await api.put(`/admin/testimonials/${editing.id}`, payload);
            else await api.post("/admin/testimonials", payload);
            toast.success(editing ? "Atualizado" : "Criado"); setModalOpen(false); load();
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro"); }
        finally { setSaving(false); }
    }
    async function onDelete(it) {
        if (!window.confirm(`Apagar testemunho de ${it.name}?`)) return;
        try { await api.delete(`/admin/testimonials/${it.id}`); toast.success("Apagado"); load(); }
        catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro"); }
    }
    async function togglePublished(it) {
        try { await api.put(`/admin/testimonials/${it.id}`, { published: !it.published }); load(); }
        catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro"); }
    }
    async function toggleFeatured(it) {
        try { await api.put(`/admin/testimonials/${it.id}`, { featured: !it.featured }); load(); }
        catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro"); }
    }

    const { dragProps, draggingIndex, overIndex } = useDragReorder({ items, setItems, collection: "testimonials" });

    return (
        <div data-testid="admin-testimonials-page">
            <PageHeader title="Testemunhos" subtitle="Cards exibidos em /sobre e em destaque na home — arraste para reordenar" onAdd={openCreate} addLabel="Novo testemunho" addTestId="testimonial-add" count={items.length} />

            {loading ? (
                <div className="mt-4 p-8 text-center text-muted-foreground rounded-2xl border border-border bg-white">A carregar...</div>
            ) : (
                <div className="mt-4 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                    {items.map((it, idx) => (
                        <div key={it.id} data-testid={`testimonial-card-${it.id}`} {...dragProps(idx)}
                            className={`rounded-2xl border bg-white p-5 flex gap-4 transition ${draggingIndex === idx ? "opacity-40" : ""} ${overIndex === idx && draggingIndex !== null && draggingIndex !== idx ? "border-secondary ring-2 ring-secondary/40" : "border-border"}`}>
                            <span data-testid={`testimonial-drag-${it.id}`} title="Arrastar para reordenar"
                                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-primary self-start pt-1 -ml-1">
                                <GripVertical className="h-4 w-4" />
                            </span>
                            {it.photo_url ? (
                                <img src={it.photo_url} alt={it.name} className="h-14 w-14 rounded-full object-cover ring-2 ring-secondary/40 flex-shrink-0" />
                            ) : (
                                <div className="h-14 w-14 rounded-full bg-muted flex-shrink-0 flex items-center justify-center font-heading font-bold text-primary">
                                    {it.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="font-heading font-bold text-primary truncate">{it.name}</div>
                                        <div className="text-xs text-muted-foreground truncate">{it.role}</div>
                                        {it.niche && (
                                            <span className="mt-1 inline-flex items-center rounded-full bg-secondary/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                                                {NICHE_OPTIONS.find((o) => o.value === it.niche)?.label || it.niche}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button onClick={() => toggleFeatured(it)} title={it.featured ? "Remover destaque" : "Destacar"}
                                            className={`rounded-md p-1.5 ${it.featured ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`}>
                                            <Star className={`h-4 w-4 ${it.featured ? "fill-amber-400" : ""}`} />
                                        </button>
                                    </div>
                                </div>
                                <p className="mt-2 text-sm text-muted-foreground line-clamp-3">“{it.quote}”</p>
                                <div className="mt-3 flex items-center gap-2 flex-wrap">
                                    <button onClick={() => togglePublished(it)} className={`text-xs rounded px-2 py-1 font-bold ${it.published ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                                        {it.published ? <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> Publicado</span> : <span className="inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> Oculto</span>}
                                    </button>
                                    <button onClick={() => openEdit(it)} data-testid={`testimonial-edit-${it.id}`} className="text-xs rounded px-2 py-1 bg-muted hover:bg-zinc-200 inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Editar</button>
                                    <button onClick={() => onDelete(it)} data-testid={`testimonial-delete-${it.id}`} className="text-xs rounded px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Apagar</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Editar testemunho" : "Novo testemunho"} size="lg">
                <form onSubmit={onSave} className="space-y-4" data-testid="testimonial-form">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Nome">
                            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                        </Field>
                        <Field label="Cargo / contexto">
                            <input required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                        </Field>
                    </div>
                    <Field label="Citacão">
                        <textarea required rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary resize-y" />
                    </Field>
                    <Field label="URL da foto (opcional)">
                        <input type="url" value={form.photo_url || ""} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                    </Field>
                    <div className="grid grid-cols-3 gap-4">
                        <Field label="Nicho">
                            <select
                                value={form.niche || ""}
                                onChange={(e) => setForm({ ...form, niche: e.target.value })}
                                className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary"
                                data-testid="testimonial-niche"
                            >
                                {NICHE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Ordem">
                            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary" />
                        </Field>
                        <Field label="Destaque">
                            <label className="flex items-center gap-2 mt-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Mostrar na Home</label>
                        </Field>
                    </div>
                    <div className="grid grid-cols-1">
                        <Field label="Publicado">
                            <label className="flex items-center gap-2 mt-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Visível</label>
                        </Field>
                    </div>
                    <div className="flex justify-end gap-3 pt-3 border-t border-border">
                        <button type="button" onClick={() => setModalOpen(false)} className="px-5 py-2.5 text-sm font-semibold rounded-md border border-border hover:bg-muted">Cancelar</button>
                        <button type="submit" disabled={saving} data-testid="testimonial-save" className="px-6 py-2.5 text-sm font-bold rounded-md bg-primary text-primary-foreground hover:-translate-y-0.5 transition disabled:opacity-60">
                            {saving ? "A guardar..." : (editing ? "Atualizar" : "Criar")}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
